import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/errors';
import type { DocumentFolderTemplate as GeneratedDocumentFolderTemplate } from '@/api/generated/client/models/DocumentFolderTemplate';

// Re-export the generated type so existing consumers don't break
export type DocumentFolderTemplate = GeneratedDocumentFolderTemplate;

// ── Param interfaces ─────────────────────────────────────────────

interface CreateTemplateParams {
  name: string;
  parentId?: number | null;
  color?: string | null;
}

interface UpdateTemplateParams {
  templateId: number;
  name?: string;
  parentId?: number | null;
  color?: string | null;
  isActive?: boolean;
}

// ── Hook ─────────────────────────────────────────────────────────

export function useDocumentFolderTemplates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ── Query: list templates ──
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['document-folder-templates'],
    queryFn: async () => {
      const res = await apiClient.documents.listDocumentFolderTemplates();
      return (res.items ?? []) as DocumentFolderTemplate[];
    },
  });

  const templates: DocumentFolderTemplate[] = data ?? [];

  // ── Mutation: create template ──
  const createMutation = useMutation({
    mutationFn: async (params: CreateTemplateParams) => {
      return apiClient.documents.createDocumentFolderTemplate({
        createDocumentFolderTemplateRequest: params,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folder-templates'] });
      toast({ title: 'Template Created', description: 'Folder template added successfully.' });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Create Template', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  // ── Mutation: update template ──
  const updateMutation = useMutation({
    mutationFn: async ({ templateId, ...body }: UpdateTemplateParams) => {
      return apiClient.documents.updateDocumentFolderTemplate({
        templateId,
        updateDocumentFolderTemplateRequest: body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folder-templates'] });
      toast({ title: 'Template Updated', description: 'Folder template updated successfully.' });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Update Template', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  // ── Mutation: delete template ──
  const deleteMutation = useMutation({
    mutationFn: async (templateId: number) => {
      await apiClient.documents.deleteDocumentFolderTemplate({ templateId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folder-templates'] });
      toast({ title: 'Template Deleted', description: 'Folder template removed successfully.' });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Delete Template', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  return {
    templates,
    isLoading,
    error,
    refetch,
    createTemplate: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateTemplate: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteTemplate: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
